import React from "react";

import { useSelector } from "react-redux";
import { IPolicyDriverAbstracts } from "../../../../../../redux/policyCommon/policyCommon.types";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../hooks";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import { fetchPolicyArsExtractListAsync } from "../../../../../../redux/policyCommon/policyCommonActions";
import { DataTableV2RowRenderType, IDataTableV2Props } from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { LoadState } from "../../../../../../constants/enums";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import { Button, Stack } from "@mui/material";
import { AddOutlined } from "@mui/icons-material";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import { PolicyAbstractsPreview } from "./PolicyAbstractsPreview";
import { PolicyAbstractUploadDialog } from "./PolicyAbstractUploadDialog";

export const PolicyAbstracts: React.FC<{
  customerId: number;
  customerPolicyId: number;
  /**
   * Feature: lead-commercial-auto-risk-profile
   * Optional lead scope. When a non-empty `leadId` is supplied the abstracts
   * list is fetched keyed by `lead_id` and the `customer_policy_id`-keyed fetch
   * is never issued. Absent a `leadId` the component behaves exactly as before
   * (keyed by `customer_policy_id`), preserving customer-policy behavior.
   */
  leadId?: string;
}> = ({ customerId, customerPolicyId, leadId }) => {
  const [open, setOpen] = React.useState(false);
  const [openView, setOpenView] = React.useState<IPolicyDriverAbstracts | null>(
    null
  );

  const { list, loading } = useSelector(
    (storeState: IStoreState) => storeState.policyCommon.abstracts
  );
  const dispatch = useDispatchWrapper();

  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE"
  );

  const {
    state: { dateState, searchState, pagination, tabs, columnsConfig,
      dense },
    setDateState,
    setSearchState,
    onPageChange,
    onRowsPerPageChange,
    setSelectedTab,
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last90Days",
      selectedTab: "-1",
    },
  });

  const fetchList = () => {
    // Lead scope wins: fetch keyed by `lead_id` and never issue the
    // `customer_policy_id`-keyed request. Otherwise the original
    // customer-policy fetch runs unchanged (non-regression).
    if (leadId) {
      dispatch(fetchPolicyArsExtractListAsync(0, status, leadId));
    } else if (customerPolicyId) {
      dispatch(fetchPolicyArsExtractListAsync(customerPolicyId, status));
    }
  };

  const policyAbstractsTableProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    extraFetchFactor: pagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "none",
    uniqueRowKeyName: "incemental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS}
              />
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPolicyDriverAbstracts) => {
          return (
            <StandardTableActions
              onEditClick={() => {
                setOpenView(row);
              }}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPolicyDriverAbstracts) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "licence",
        headerName: "Licence Number",
        fieldName: "licence",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "name",
        headerName: "Name",
        fieldName: "name",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "gender",
        headerName: "Gender",
        fieldName: "gender",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "address",
        headerName: "Address",
        fieldName: "address",
        renderType: DataTableV2RowRenderType.TEXT,
      },

      {
        key: "birthdate",
        headerName: "Birth Date",
        fieldName: "birthdate",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "height",
        headerName: "Height",
        fieldName: "height",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
    ],
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "create_ts",
      },
    },
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  const handleComplete = () => {
    fetchList();
    setOpen(false);
  };

  React.useEffect(() => {
    fetchList();
    //   dispatch(fetchBindersListAsync(customerPolicyId));
    // dispatch(fetchCommAutomobileCustomerInfoAsync(customerPolicyId));
  }, [customerPolicyId, leadId, status]);

  return (
    <>
      <PageLoader
        loading={false}
        // loading={customerInfo.loading === LoadState.InProgress}
        //error={customerInfo.error ? { message: customerInfo.error } : null}
      >
        <PageTitleBar
          heading="Abstracts"
          rightHeading={
            <Stack direction={"row"} spacing={1}>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setOpen(true)}
                startIcon={<AddOutlined />}
              >
                Create
              </Button>
            </Stack>
          }
        />

        <DataTableV2 {...policyAbstractsTableProps} />

        {open && (
          <PolicyAbstractUploadDialog
            open={open}
            onComplete={handleComplete}
            type={"commauto"}
            customerId={customerId}
            customerPolicyId={customerPolicyId}
            onClose={() => setOpen(false)}
          />
        )}
        {openView && (
          <PolicyAbstractsPreview
            open={true}
            data={openView}
            onClose={() => setOpenView(null)}
          />
        )}
      </PageLoader>
    </>
  );
};
